/**
 * Hatch pattern library persistence — user preference state (persisted),
 * NOT document state. Stores the parsed definitions of imported .pat files
 * so HATCH / HATCHEDIT / Filled Regions can resolve pattern names against
 * the user's own pattern library without re-importing.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { BUILT_IN_PAT_PATTERNS, type PatPattern } from '../utils/hatchPat';

export interface HatchPatternFileEntry {
    fileName: string;
    importedAt: number;
    /** Definitions parsed from the file, first-occurrence-wins order. */
    patterns: PatPattern[];
}

interface HatchPatternState {
    files: HatchPatternFileEntry[];
    /** Add/replace the entry of a file (re-import updates in place). */
    importFile: (entry: HatchPatternFileEntry) => void;
    removeFile: (fileName: string) => void;
    clear: () => void;
}

export const useHatchPatternStore = create<HatchPatternState>()(
    persist(
        (set) => ({
            files: [],
            importFile: (entry) =>
                set((state) => ({
                    files: [...state.files.filter((f) => f.fileName !== entry.fileName), entry],
                })),
            removeFile: (fileName) =>
                set((state) => ({ files: state.files.filter((f) => f.fileName !== fileName) })),
            clear: () => set({ files: [] }),
        }),
        { name: 'yap-hatch-patterns' }
    )
);

/**
 * Resolve a pattern name (case-insensitive) to its definition: imported .pat
 * files first (user definitions shadow built-ins), then the app built-ins
 * that back the stock region/hatch pattern list. Returns null for SOLID and
 * unknown names — callers fall back to their legacy rendering.
 */
export function resolveHatchPattern(name: string): PatPattern | null {
    const key = name.trim().toUpperCase();
    if (key === '' || key === 'SOLID' || key === 'SOLID FILL') return null;
    const { files } = useHatchPatternStore.getState();
    for (const file of files) {
        const hit = file.patterns.find((p) => p.name.toUpperCase() === key);
        if (hit) return hit;
    }
    return BUILT_IN_PAT_PATTERNS.find((p) => p.name.toUpperCase() === key) ?? null;
}
